import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  px,
  SelectField,
  simpleHeader,
  TextField,
  Tooltip,
  useModal,
} from "@homebound/beam";
import { useSuspense } from "@rest-hooks/react";
import { useMemo, useState } from "react";
import { UwConfigOptionsEndpoint, UwConfigRow, UwConfigRowsEndpoint } from "./UwConfigEndpoints";
import { formatAttribution, formatDate, UwConfigRowModal } from "./UwConfigRowModal";
import { UwConfigImportExport } from "./UwConfigImportExport";
import { formatValue, UwConfigTypeDef } from "./uwConfigFields";

const ALL = "__all__";

type UwConfigTableProps = {
  typeDef: UwConfigTypeDef;
};

export function UwConfigTable({ typeDef }: UwConfigTableProps) {
  const { rows, version } = useSuspense(UwConfigRowsEndpoint, { configType: typeDef.type });
  const { options } = useSuspense(UwConfigOptionsEndpoint, { configType: typeDef.type });
  const { openModal } = useModal();
  const filterFields = useMemo(
    () => typeDef.filters.map((name) => typeDef.fields.find((f) => f.name === name)!),
    [typeDef],
  );
  // the selected (formatted) value of each filter field, or ALL
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState<string | undefined>();

  const filterOptions = useMemo(
    () =>
      Object.fromEntries(
        filterFields.map((f) => {
          const values = [...new Set(rows.map((r) => formatValue(f, r[f.name])))].sort();
          return [
            f.name,
            [{ id: ALL, name: `All ${f.label.toLowerCase()}s` }, ...values.map((v) => ({ id: v, name: v }))],
          ];
        }),
      ),
    [filterFields, rows],
  );

  // filtered here rather than with the table's `filter` so the row count matches what's shown
  const filteredRows = useMemo(() => {
    const terms = (search ?? "").toLowerCase().split(/\s+/).filter(Boolean);
    const shown = rows.filter((r) => {
      for (const f of filterFields) {
        const selected = filters[f.name] ?? ALL;
        if (selected !== ALL && formatValue(f, r[f.name]) !== selected) return false;
      }
      const text = [...typeDef.fields.map((f) => formatValue(f, r[f.name])), r.updated_by ?? ""]
        .join(" ")
        .toLowerCase();
      return terms.every((term) => text.includes(term));
    });
    return typeDef.sortBy ? sortRows(shown, typeDef.sortBy) : shown;
  }, [rows, filters, filterFields, search, typeDef]);

  const columns = useMemo(
    () =>
      createColumns(typeDef, (row) =>
        openModal({ content: <UwConfigRowModal typeDef={typeDef} options={options} row={row} /> }),
      ),
    [typeDef, options, openModal],
  );

  return (
    <div css={Css.dg.gap2.mw0.$}>
      <div css={Css.gray700.sm.$}>{typeDef.description}</div>
      <div css={Css.df.aife.jcsb.gap2.fww.$}>
        <div css={Css.df.aife.gap2.$}>
          {filterFields.map((f) => (
            <div css={Css.w(px(280)).$} key={f.name}>
              <SelectField
                label={f.label}
                value={filters[f.name] ?? ALL}
                onSelect={(v) => setFilters((current) => ({ ...current, [f.name]: v ?? ALL }))}
                options={filterOptions[f.name]}
              />
            </div>
          ))}
          <div css={Css.w(px(280)).$}>
            <TextField label="Search" value={search} onChange={setSearch} clearable />
          </div>
          <div css={Css.sm.gray700.pb1.$} data-testid="rowCount">
            {filteredRows.length} of {rows.length} rows · version {version}
          </div>
        </div>
        <div css={Css.df.gap1.$}>
          <Button
            label="Add Row"
            icon="plus"
            onClick={() => openModal({ content: <UwConfigRowModal typeDef={typeDef} options={options} /> })}
          />
          <UwConfigImportExport typeDef={typeDef} />
        </div>
      </div>
      {/* only the table scrolls, so the filters and buttons above stay in view however wide the config is */}
      <div css={Css.oa.mw0.maxh("calc(100vh - 320px)").$} data-testid="tableScroll">
        <GridTable
          columns={columns}
          rows={createRows(filteredRows)}
          stickyHeader
          sorting={{ on: "client" }}
          fallbackMessage="No rows match"
        />
      </div>
    </div>
  );
}

/** Sort by the given fields, with null (e.g. "all property types") before any value */
function sortRows(rows: UwConfigRow[], fields: string[]): UwConfigRow[] {
  const sortValue = (value: unknown) => (value === null || value === undefined ? "" : `~${value}`);
  return [...rows].sort((a, b) => {
    for (const f of fields) {
      const order = sortValue(a[f]).localeCompare(sortValue(b[f]));
      if (order !== 0) return order;
    }
    return a.id - b.id;
  });
}

type Row = { kind: "header" } | { kind: "data"; data: UwConfigRow };

function createRows(rows: UwConfigRow[]): GridDataRow<Row>[] {
  return [simpleHeader, ...rows.map((r) => ({ kind: "data" as const, id: `${r.id}`, data: r }))];
}

function createColumns(typeDef: UwConfigTypeDef, onEdit: (row: UwConfigRow) => void): GridColumn<Row>[] {
  return [
    // first and pinned (with the fields that identify a row) so editing never needs a horizontal scroll
    column<Row>({
      id: "edit",
      header: "",
      data: (row) => <Button variant="text" label="Edit" icon="pencil" onClick={() => onEdit(row)} />,
      clientSideSort: false,
      sticky: "left",
      w: "90px",
    }),
    ...typeDef.fields.map((f) =>
      column<Row>({
        id: f.name,
        header: f.label,
        data: (row) => {
          const text = formatValue(f, row[f.name]);
          // e.g. "default (all property types)" is shown in gray, so a row's specific values stand out
          const isDefault = row[f.name] === null && !!f.nullLabel;
          return {
            content: () => (
              <Tooltip title={formatAttribution(row.field_updates[f.name])}>
                <span css={isDefault ? Css.gray700.$ : undefined}>{text}</span>
              </Tooltip>
            ),
            value: text,
          };
        },
        ...(f.pinned
          ? { sticky: "left" as const, w: "220px" }
          : { w: f.kind === "text" ? 2 : 1, mw: f.kind === "text" ? "160px" : "100px" }),
      }),
    ),
    column<Row>({
      id: "updated",
      header: "Last Changed",
      data: (row) => ({
        content: (
          <div css={Css.xs.$}>
            <div>{row.updated_by ?? "Original config"}</div>
            <div css={Css.gray700.$}>{formatDate(row.updated_at)}</div>
          </div>
        ),
        value: row.updated_by ?? "",
        sortValue: row.updated_at,
      }),
      w: "200px",
    }),
  ];
}
